/**
 * tool_lite_offer_v1 A/B test (started 2026-10-28): does a $10/month Lite plan
 * (500 credits a month, server plan lite_monthly) in a small out-of-credits
 * pop-up on the image and video tool pages turn more of those visitors into
 * buyers than today's upgrade modal?
 *
 * Oct 28 2026 read (Sep 28 – Oct 27): the tool pages' upgrade modal opened for
 * 977 people, 973 of them on the yearly plans tab (accounts that never paid
 * open there). People whose first paywall was a tool page: 886 → 1 buyer
 * (0.1%), against 17,343 → 591 (3.4%) for every other first paywall. A tool
 * run costs 5–36 credits; the smallest offer was a $30 pack or Plus at $35.
 * Competitors start at $8–15 a month.
 *
 * "control" is today's UpgradeModal. "lite" is ToolLiteOffer: Lite at
 * $10/month first, "Or buy credits once" (the modal on its top-up tab) and
 * "See all plans" (the modal as today). Lite has the same features as Plus
 * (720p/1080p, no watermark, commercial use) and its credits work everywhere,
 * music videos included. It is never shown on the pricing page or in the
 * upgrade modal (server/litePlan.ts refuses it for accounts that paid).
 *
 * Who is in the test: anyone the tool pages' out-of-credits pop-up opens for
 * who could buy Lite — signed-out visitors (priced out before signing up),
 * and signed-in accounts the server calls eligible (payment.liteOfferStatus:
 * never paid, free plan, no Stripe subscription). Paid accounts keep today's
 * modal and are never drawn.
 *
 * The browser is the unit: drawn on the first such pop-up and kept in
 * localStorage. A signed-in account draws from a salted FNV-1a bucket of its
 * user id, so it gets the same arm on every device it signs in on.
 *
 * Kill switch: VITE_TOOL_LITE_OFFER_PERCENT=0 (build-time, default 50) shows
 * everyone today's modal, draws nothing and stamps nothing. Other values only
 * decide new draws; stored arms stay.
 *
 * Readout: `tool_lite_offer_exposure` (experiment, arm, tool, signed_in) on
 * every pop-up in either arm, and the person property tool_lite_offer_v1_arm
 * (set once on the exposure, and again at sign-up/in through useAuth, since
 * PostHog drops $set_once for anonymous visitors). Then `purchase` and
 * `credit_topup_purchased` per exposed person by arm, and Lite = plan
 * lite_monthly; revenue per exposed person is the main metric.
 */

import { fnv1a32 } from "./directorStyleBoost";

export const TOOL_LITE_OFFER_EXPERIMENT = "tool_lite_offer_v1";

export type ToolLiteOfferArm = "control" | "lite";

export const TOOL_LITE_OFFER_STORAGE_KEY = `solmi:exp:${TOOL_LITE_OFFER_EXPERIMENT}`;

/** The person property carrying the arm. */
export const TOOL_LITE_OFFER_PERSON_PROPERTY = `${TOOL_LITE_OFFER_EXPERIMENT}_arm`;

/** The server plan the pop-up sells (server/stripe.ts SUBSCRIPTION_PLANS). */
export const LITE_PLAN_ID = "lite_monthly";
export const LITE_PRICE_USD = 10;
export const LITE_MONTHLY_CREDITS = 500;

export function toolLiteOfferPercent(raw: unknown): number {
  if (raw === undefined || raw === null || raw === "") return 50;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 50;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function toolLiteOfferBucket(seed: string): number {
  return fnv1a32(`${TOOL_LITE_OFFER_EXPERIMENT}:${seed}`) % 100;
}

function isArm(value: unknown): value is ToolLiteOfferArm {
  return value === "control" || value === "lite";
}

type ArmStorage = Pick<Storage, "getItem" | "setItem">;

function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Reading window.localStorage itself throws when site data is blocked.
    return null;
  }
}

function readStoredArm(storage: ArmStorage | null): ToolLiteOfferArm | null {
  try {
    const stored = storage?.getItem(TOOL_LITE_OFFER_STORAGE_KEY);
    return isArm(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Whether this pop-up may draw (or use) an arm: signed-out visitors, and
 * signed-in accounts without paid access that the server calls eligible.
 * `liteEligible` undefined = the server hasn't answered yet: not drawn, so a
 * slow answer never shows Lite to someone who can't buy it.
 */
export function toolLiteOfferAudience(input: {
  isAuthenticated: boolean;
  hasPaidAccess: boolean;
  liteEligible: boolean | undefined;
}): boolean {
  if (!input.isAuthenticated) return true;
  if (input.hasPaidAccess) return false;
  return input.liteEligible === true;
}

/**
 * This browser's arm, or null when the test is off. Reads the stored arm
 * first; otherwise draws one (from the user id when given, else at random)
 * and stores it. Storage that throws still yields an arm, just not a sticky
 * one. `storage` defaults to localStorage.
 */
export function toolLiteOfferArm(input: {
  userId?: string | number | null;
  percent: number;
  storage?: ArmStorage | null;
  random?: () => number;
}): ToolLiteOfferArm | null {
  if (input.percent <= 0) return null;
  const storage =
    input.storage === undefined ? browserStorage() : input.storage;
  const stored = readStoredArm(storage);
  if (stored) return stored;
  const userId =
    input.userId === null || input.userId === undefined
      ? ""
      : String(input.userId);
  const bucket = userId
    ? toolLiteOfferBucket(userId)
    : Math.floor((input.random ?? Math.random)() * 100);
  const arm: ToolLiteOfferArm = bucket < input.percent ? "lite" : "control";
  try {
    storage?.setItem(TOOL_LITE_OFFER_STORAGE_KEY, arm);
  } catch {}
  return arm;
}

/** What tool_lite_offer_exposure and the pop-up's clicks carry. */
export function toolLiteOfferEventProps(
  arm: ToolLiteOfferArm | null,
): Record<string, string> {
  return arm ? { experiment: TOOL_LITE_OFFER_EXPERIMENT, arm } : {};
}

/** The person property to set once, or undefined outside the test. */
export function toolLiteOfferPersonProps(
  arm: ToolLiteOfferArm | null,
): Record<string, string> | undefined {
  return arm ? { [TOOL_LITE_OFFER_PERSON_PROPERTY]: arm } : undefined;
}

/**
 * The person property to set once when a visitor signs up or in (useAuth's
 * identify call): the arm this browser already holds, undefined if none or
 * with the test off. It never draws.
 */
export function toolLiteOfferIdentifyProps(
  rawPercent: unknown,
  storage?: ArmStorage | null,
): Record<string, string> | undefined {
  if (toolLiteOfferPercent(rawPercent) <= 0) return undefined;
  return toolLiteOfferPersonProps(
    readStoredArm(storage === undefined ? browserStorage() : storage),
  );
}

/**
 * A signed-out "Get Lite" goes through sign-in first, and every sign-in ends
 * in a full page load back on the tool page (the email reload, the Google and
 * Apple redirects). This keeps the click across it, so the page can go on to
 * the Lite checkout once the server calls the account eligible. Nov 1 2026: 3
 * of the first 20 "Get Lite" clicks were signed out; 2 signed up and none
 * reached checkout, since a new account doesn't run out of credits again soon.
 */
export const TOOL_LITE_OFFER_PENDING_KEY = `${TOOL_LITE_OFFER_STORAGE_KEY}:pending`;

/** How long a signed-out "Get Lite" waits for the sign-in. */
export const TOOL_LITE_OFFER_PENDING_MAX_AGE_MS = 30 * 60_000;

export type ToolLiteOfferPending = {
  toolId: string;
  /** The pop-up's line, e.g. "This needs 45 credits. You have 12." */
  description: string;
  at: number;
};

type PendingStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function saveToolLiteOfferPending(input: {
  toolId: string;
  description: string;
  storage?: PendingStorage | null;
  now?: number;
}): void {
  const storage =
    input.storage === undefined ? browserStorage() : input.storage;
  const pending: ToolLiteOfferPending = {
    toolId: input.toolId,
    description: input.description,
    at: input.now ?? Date.now(),
  };
  try {
    storage?.setItem(TOOL_LITE_OFFER_PENDING_KEY, JSON.stringify(pending));
  } catch {}
}

/**
 * The signed-out "Get Lite" saved on `toolId`'s page, removed so it resumes
 * once. Null when there is none or it is too old (both removed), and when it
 * was saved on another tool's page (kept for that page).
 */
export function takeToolLiteOfferPending(input: {
  toolId: string;
  storage?: PendingStorage | null;
  now?: number;
}): ToolLiteOfferPending | null {
  const storage =
    input.storage === undefined ? browserStorage() : input.storage;
  let pending: Partial<ToolLiteOfferPending> | null = null;
  try {
    const raw = storage?.getItem(TOOL_LITE_OFFER_PENDING_KEY);
    if (!raw) return null;
    pending = JSON.parse(raw);
  } catch {}
  const age = (input.now ?? Date.now()) - Number(pending?.at);
  const valid =
    typeof pending?.toolId === "string" &&
    typeof pending.description === "string" &&
    age >= 0 &&
    age <= TOOL_LITE_OFFER_PENDING_MAX_AGE_MS;
  if (valid && pending?.toolId !== input.toolId) return null;
  try {
    storage?.removeItem(TOOL_LITE_OFFER_PENDING_KEY);
  } catch {}
  return valid ? (pending as ToolLiteOfferPending) : null;
}
